import React, { useState, useEffect } from 'react';
import { BookOpen, ChevronDown, ChevronUp, ArrowRight } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MODALITY_LABELS = {
  listening: { label: 'Listening', color: 'bg-cyan-50 text-cyan-800 border-cyan-200' },
  speaking: { label: 'Speaking', color: 'bg-purple-50 text-purple-800 border-purple-200' },
  reading: { label: 'Reading', color: 'bg-blue-50 text-blue-800 border-blue-200' },
  writing: { label: 'Writing', color: 'bg-orange-50 text-orange-800 border-orange-200' },
};

const LEVEL_COLORS = {
  1: 'border-red-300 bg-red-50',
  2: 'border-orange-300 bg-orange-50',
  3: 'border-yellow-300 bg-yellow-50',
  4: 'border-blue-300 bg-blue-50',
  5: 'border-indigo-300 bg-indigo-50',
  6: 'border-green-300 bg-green-50',
};

const CanDoReference = ({ modality, level, compact = false }) => {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(!compact);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!modality || !level) { setLoading(false); return; }
    axios.get(`${API}/wida/can-do/${modality}/${level}`)
      .then(r => { setData(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [modality, level]);

  if (loading || !data) return null;
  const { current, next_target } = data;
  const modInfo = MODALITY_LABELS[modality] || { label: modality, color: 'bg-gray-50 text-gray-800 border-gray-200' };
  const lvlColor = LEVEL_COLORS[current.level] || 'border-gray-300 bg-gray-50';

  if (compact) {
    return (
      <div className={`border ${lvlColor} p-3 text-xs`} data-testid={`cando-compact-${modality}-${level}`}>
        <button onClick={() => setExpanded(!expanded)} className="w-full flex items-center justify-between">
          <span className="font-bold">
            <span className={`inline-block px-1.5 py-0.5 text-[10px] border mr-1.5 ${modInfo.color}`}>{modInfo.label}</span>
            Level {current.level} — {current.level_name}: What they CAN do
          </span>
          {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </button>
        {expanded && (
          <div className="mt-2 space-y-1">
            {current.can_do.map((c, i) => (
              <div key={i} className="pl-3 text-gray-700 relative before:content-[''] before:absolute before:left-0 before:top-1.5 before:w-1.5 before:h-1.5 before:rounded-full before:bg-green-400">{c}</div>
            ))}
            {next_target && (
              <div className="mt-2 pt-2 border-t border-dashed border-gray-300">
                <div className="font-bold text-[#002FA7] flex items-center gap-1">
                  <ArrowRight size={12} /> Growth Target: Level {next_target.level} ({next_target.level_name})
                </div>
                {next_target.can_do.slice(0, 2).map((c, i) => (
                  <div key={i} className="pl-3 text-gray-500 mt-1 relative before:content-[''] before:absolute before:left-0 before:top-1.5 before:w-1.5 before:h-1.5 before:rounded-full before:bg-blue-300">{c}</div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`border-2 ${lvlColor} p-4`} data-testid={`cando-full-${modality}-${level}`}>
      <div className="flex items-center gap-3 mb-3">
        <BookOpen size={18} className="text-[#002FA7]" />
        <span className={`px-2 py-0.5 text-xs font-bold border ${modInfo.color}`}>{modInfo.label}</span>
        <h4 className="font-bold text-sm">
          Level {current.level} — {current.level_name}
        </h4>
      </div>

      <div className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">What this student CAN do:</div>
      <ul className="space-y-1.5 mb-4">
        {current.can_do.map((c, i) => (
          <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
            <span className="w-2 h-2 rounded-full bg-green-500 mt-1.5 flex-shrink-0" />
            {c}
          </li>
        ))}
      </ul>
      {current.example && (
        <div className="bg-white border border-gray-200 p-3 mb-4 text-xs text-gray-600 italic">
          <span className="font-bold text-gray-700 not-italic">Example: </span>{current.example}
        </div>
      )}

      {next_target && (
        <>
          <div className="text-xs font-bold uppercase tracking-wider text-[#002FA7] mb-2 flex items-center gap-1">
            <ArrowRight size={12} /> Growth Target: Level {next_target.level} ({next_target.level_name})
          </div>
          <ul className="space-y-1.5">
            {next_target.can_do.map((c, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-gray-500">
                <span className="w-2 h-2 rounded-full bg-blue-400 mt-1.5 flex-shrink-0" />
                {c}
              </li>
            ))}
          </ul>
          {next_target.example && (
            <div className="bg-blue-50 border border-blue-200 p-3 mt-2 text-xs text-gray-600 italic">
              <span className="font-bold text-blue-700 not-italic">Next Level Example: </span>{next_target.example}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CanDoReference;
